import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  getPartnersList,
  getPartnerMetaData,
  saveOrUpdatePartner,
  getPartnerDetailsById,
} from '../services/PartnerService';
import type {
  PartnerList,
  PartnerMetaDate,
  PartnerData,
} from '../services/PartnerService';

export interface PartnersListQueryPerams {
  userId: string;
  page?: number;
  size?: number;
  sortField?: string;
  sortType?: string;
  searchTerm?: string;
  statusId?: number[];
}

export const useGetPartnersList = ({
  userId,
  page = 0,
  size = 10,
  sortField,
  sortType,
  searchTerm,
  statusId,
}: PartnersListQueryPerams) => {
  const { data, isLoading, isError, error, refetch } = useQuery<PartnerList>({
    queryKey: [
      'PartnersListResponse',
      userId,
      page,
      size,
      sortField,
      sortType,
      searchTerm,
      statusId,
    ],
    queryFn: async () => {
      const data = await getPartnersList(
        userId,
        page,
        size,
        sortField,
        sortType,
        searchTerm,
        statusId
      );
      return data;
    },
    staleTime: 0,
    gcTime: 0,
  });

  return {
    PartnersListResponse: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useGetPartnerMetaData = (userId: string) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<PartnerMetaDate>({
      queryKey: ['PartnerMetaData', userId],
      queryFn: async () => {
        const data = await getPartnerMetaData(userId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
    });

  return {
    PartnerMetaData: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useSaveOrUpdatePartner = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  return useMutation<
    PartnerData,
    Error,
    PartnerData & { userId: string; action: string }
  >({
    mutationFn: async ({ userId, action, ...payload }) => {
      const data = await saveOrUpdatePartner(userId, action, payload);
      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['PartnersListResponse'],
      });
      onSuccess();
    },
    onError: (error) => {
      console.error('Error saving or updating partner:', error);
    },
  });
};

export const useGetPartnerDetailsById = (userId: string, partnerId: number) => {
  const { data, isLoading, isError, error, refetch } = useQuery<PartnerData>({
    queryKey: ['PartnerDetailsById', userId, partnerId],
    queryFn: async () => {
      const data = await getPartnerDetailsById(userId, partnerId);
      return data;
    },
    staleTime: 0,
    gcTime: 0,
    enabled: !!partnerId,
  });

  return {
    PartnerDetailsById: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};
